import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { updateEmailMutation } from '@/mutations/user.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useFormik } from 'formik';
import { analytics } from '@/lib/analytics';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const ChangeEmailDialog: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const { dismissActiveDialog } = useDialog();
  const { t } = useTranslation('account');

  const formik = useFormik({
    initialValues: {
      newEmail: '',
    },
    onSubmit: async (values) => {
      const toastId = toast.loading(t('changeEmail.changingEmail'));
      try {
        const result = await execute(updateEmailMutation, { id: userId, email: values.newEmail });
        if (!result.data?.updateEmail) {
          toast.error(t('changeEmail.failedChangeEmail'), {
            description: t('changeEmail.failedChangeEmailDescription'),
            id: toastId,
          });
        } else {
          analytics.capture('Email Change Requested');
          toast.success(t('changeEmail.verificationSent'), {
            id: toastId,
            description: t('changeEmail.verificationSentDescription'),
          });
        }
      } catch (error) {
        const rawMessage = error instanceof Error ? error.message : '';
        const isDisposable = rawMessage.includes('DISPOSABLE_EMAIL_DOMAIN');
        toast.error(t('changeEmail.failedChangeEmail'), {
          id: toastId,
          description: isDisposable ? t('changeEmail.disposableEmail') : t('changeEmail.failedChangeEmailDescription'),
        });
      }
      dismissActiveDialog();
    },
  });

  return (
    <div className='space-y-4'>
      <div>
        <h3 className='text-lg font-semibold'>{t('changeEmail.title')}</h3>
        <p className='text-gray-600'>{t('changeEmail.description')}</p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='newEmail'>{t('changeEmail.newEmail')}</Label>
          <Input
            id='newEmail'
            name='newEmail'
            type='email'
            value={formik.values.newEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('changeEmail.placeholder')}
          />
        </div>
        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            {t('changeEmail.cancel')}
          </Button>
          <Button type='submit' disabled={formik.isSubmitting}>
            {t('changeEmail.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};
